import { useLocation } from '@reach/router';
import React, { useEffect, useMemo, useState } from 'react';

import { IconPointCircle } from '../components/Icons';

import Layout from '../components/Layout';
import Toc from '../components/Toc';
import SEO from '../components/seo';

import { MDXRenderer } from 'gatsby-plugin-mdx';
import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import { BACK, CLI_PAGE, SIDEBAR_CLI, SIDEBAR_SOFTWARE, SOFTWARE_PAGE } from '../utils/constants';

import { graphql } from 'gatsby';
import InfoBanner from '../components/InfoBanner/InfoBanner';
import SideBar from '../components/SideBar/SideBar';
import { capitalize, getCliSideBarDataRefactored } from '../utils/functions';
import './cli.scss';
import './tutorial.scss';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import boardManagerImg from './img/boards-manager-screenshot.png';
import libraryLayout from './img/library-layout.svg';
import pluggableImg from './img/pluggable-discovery-state-machine.png';

interface CliProps {
  pageContext: {
    content: any;
    name: string;
    version: string;
    tocData: any;
  };
  data: any;
}

function actualPageName(title) {
  if (title === 'getting-started') return 'Getting started';
  if (title === 'CONTRIBUTING') return 'How to Contribute';
  if (title === 'FAQ') return 'FAQ';
  if (title === 'versioning') return 'Backward compatibility policy';
  return capitalize(title.replace(/[-_]/g, ' ').toLowerCase());
}

export default function Cli({ pageContext, data }: CliProps): React.ReactElement {
  const pageName = actualPageName(pageContext.name);
  const sideBarData = getCliSideBarDataRefactored(data.allCliDataMarkdown.nodes);
  const location = useLocation();

  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const isDesktop = useMemo(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 768;
  }, []);

  const buildBreadcrumbs = useMemo(() => {
    return [
      { title: 'Home', url: '/' },
      { title: SIDEBAR_SOFTWARE, url: SOFTWARE_PAGE },
      { title: SIDEBAR_CLI, url: CLI_PAGE },
      { title: pageName, url: null },
    ];
  }, [pageName]);

  // Pages are fetched from arduino-cli websites, so we have to convert links and imgs.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const container = document.querySelector('.tutorial__text');
    if (container) {
      // Update links into a to set correct basePath
      const links = container.querySelectorAll('a');
      links.forEach((link) => {
        const originalHref = link.getAttribute('href');
        if (originalHref && originalHref.slice(-3) === '.md') {
          const newHref = '/arduino-cli/' + originalHref.slice(0, -3);
          link.setAttribute('href', newHref);
        }

        // Update .md into links
        if (originalHref && originalHref.indexOf('.md') > -1) {
          const newHref = '/arduino-cli/' + originalHref.replace('.md', '');
          link.setAttribute('href', newHref);
        }
      });

      // Add codeblocks language to highlight code
      const codes = container.querySelectorAll('code');
      codes.forEach((code) => {
        const originalLanguage = code.getAttribute('class');
        if (!originalLanguage) {
          const newLanguage = 'language-arduino';
          code.setAttribute('class', newLanguage);
        }
      });

      // Update img path
      const imgs = container.querySelectorAll('img');
      imgs.forEach((img) => {
        if (pageName === 'Library specification') img.setAttribute('src', libraryLayout);
        else if (pageName === 'Pluggable discovery specification') img.setAttribute('src', pluggableImg);
        else if (pageName === 'Package index json specification') img.setAttribute('src', boardManagerImg);
      });
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const tutorialTextDiv = document.querySelector('.tutorial__text');

    // gRPC CLI reference have a lot of useless stuff.
    if (tutorialTextDiv && location.pathname === '/arduino-cli/commands/') {
      let firstCount = 23;

      while (firstCount > 0 && tutorialTextDiv.firstChild) {
        tutorialTextDiv?.removeChild(tutorialTextDiv.firstChild);
        firstCount--;
      }

      let count = 8;
      while (count > 0 && tutorialTextDiv.lastChild) {
        tutorialTextDiv?.removeChild(tutorialTextDiv.lastChild);
        count--;
      }
    }
  }, [location.pathname]);

  return (
    <Layout>
      <SEO title={pageName} description={'Arduino CLI'} />
      <main className="page-container">
        <SideBar
          links={[]}
          actualPage={pageName}
          prevPage={{ title: BACK, pathname: CLI_PAGE }}
          nestedCategories={sideBarData}
          selectedItem={pageName}
          alwaysOpen
        />
        <div className="page-layout tutorial cli">
          <div className="tutorial__container">
            <div className="tutorial__content">
              <Breadcrumbs actualPage={pageName} list={buildBreadcrumbs} />
              <h1>{pageName}</h1>
              <div className="tutorial__metadata">
                <div className="tutorial__metadata--author">
                  Author <IconPointCircle /> Arduino
                </div>
                <div>{pageContext.version}</div>
              </div>
              {!hydrated && <CustomCLISkeleton />}
              <div className="tutorial__text">
                <MDXRenderer>{pageContext?.content?.childMdx?.body}</MDXRenderer>
              </div>
              <InfoBanner
                title={
                  "In this page you can check the latest version of the Arduino CLI. You can find previous versions <a href='https://arduino.github.io/arduino-cli'>here</a>."
                }
              />
              <br />
            </div>
            {isDesktop && (
              <div className="tutorial__toc">
                <Toc toc={pageContext.tocData}></Toc>
              </div>
            )}
          </div>
        </div>
      </main>
    </Layout>
  );
}

export const query = graphql`
  query {
    allCliDataMarkdown {
      nodes {
        id
        name
        category
        cliBody {
          __typename
          childMdx {
            body
          }
        }
      }
    }
  }
`;

export function CustomSkeleton(): React.ReactElement {
  return (
    <>
      <div className="tutorial__skeleton">
        <Skeleton height={24} className={'tutorial__skeleton--long'} />
        <Skeleton height={24} width={24} />
      </div>
      <div className="tutorial__skeleton">
        <Skeleton height={24} className={'tutorial__skeleton--long'} />
        <Skeleton height={24} width={24} />
      </div>
      <div className="tutorial__skeleton">
        <Skeleton height={24} className={'tutorial__skeleton--long'} />
        <Skeleton height={24} width={24} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton">
        <Skeleton height={24} className={'tutorial__skeleton--long'} />
        <Skeleton height={24} width={24} />
      </div>
      <div className="tutorial__skeleton">
        <Skeleton height={24} className={'tutorial__skeleton--long'} />
        <Skeleton height={24} width={24} />
      </div>
      <div className="tutorial__skeleton">
        <Skeleton height={24} className={'tutorial__skeleton--long'} />
        <Skeleton height={24} width={24} />
      </div>
    </>
  );
}

export function CustomCLISkeleton(): React.ReactElement {
  return (
    <>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={2} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={150} className={'tutorial__skeleton__body--space'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={2} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={1} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={2} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={150} className={'tutorial__skeleton__body--space'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={2} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={1} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={2} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={150} className={'tutorial__skeleton__body--space'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={2} />
      </div>
      <div className="tutorial__skeleton__short">
        <Skeleton height={24} width={24} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
        <Skeleton height={24} className={'tutorial__skeleton--short'} />
      </div>
      <div className="tutorial__skeleton__body">
        <Skeleton height={24} className={'tutorial__skeleton--long tutorial__skeleton__body--space'} count={1} />
      </div>
    </>
  );
}

export function CustomLoader(): React.ReactElement {
  return (
    <>
      <div className="tutorial__loader">
        <div className="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </>
  );
}
